import { useCallback, useEffect, useMemo, useState } from 'react';
import { settingsApi, GlobalSetting, CreateSettingPayload } from '../../api/settings';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, SlidersHorizontal } from 'lucide-react';
import { getErrorMessage } from '@/lib/errors';
import { useConfirm } from '@/components/shared/ConfirmDialog';
import { EmptyState, ErrorState, FormField, PageHeader } from '@/components/app';

const settingLabels: Record<string, string> = {
  showOnlyRevenueCat: 'Только RevenueCat',
};

// Пропсы маршрута (App.tsx) не используются: тема приходит из CSS-переменных.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function SettingsPage(_props: Record<string, unknown>) {
  const confirm = useConfirm();
  const [settings, setSettings] = useState<GlobalSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<CreateSettingPayload>({
    key: '',
    value: false,
    description: '',
  });

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await settingsApi.list();
      setSettings(data.settings);
    } catch (error) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить настройки'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const settingsByKey = useMemo(() => {
    return new Map(settings.map(setting => [setting.key, setting]));
  }, [settings]);

  const resetCreateForm = () => {
    setCreateForm({ key: '', value: false, description: '' });
    setKeyError(null);
  };

  const handleCreate = async () => {
    if (isCreating) return;
    if (!createForm.key.trim()) {
      setKeyError('Укажите ключ');
      return;
    }
    if (settingsByKey.has(createForm.key.trim())) {
      setKeyError('Настройка с таким ключом уже есть, измените её в списке');
      return;
    }
    setIsCreating(true);
    try {
      const payload: CreateSettingPayload = {
        key: createForm.key.trim(),
        value: createForm.value,
        description: createForm.description?.trim() || undefined,
      };
      const created = await settingsApi.create(payload);
      setSettings(prev => [...prev, created].sort((a, b) => a.key.localeCompare(b.key)));
      toast.success('Настройка создана');
      resetCreateForm();
      setIsCreateDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось создать настройку'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggle = async (key: string, value: boolean) => {
    const ok = await confirm({
      title: `${value ? 'Включить' : 'Выключить'} «${settingLabels[key] || key}»?`,
      description: 'Настройка глобальная: изменение сразу применится ко всем пользователям приложения.',
      confirmLabel: value ? 'Включить' : 'Выключить',
      destructive: true,
    });
    if (!ok) return;

    setUpdatingKeys(prev => new Set(prev).add(key));
    try {
      const updated = await settingsApi.update(key, value);
      setSettings(prev => prev.map(setting => (setting.key === key ? updated : setting)));
      toast.success('Настройка обновлена');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось обновить настройку'));
      const rollback = settingsByKey.get(key);
      if (rollback) {
        setSettings(prev => prev.map(setting => (setting.key === key ? rollback : setting)));
      }
    } finally {
      setUpdatingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const renderSettingRow = (setting: GlobalSetting) => {
    const label = settingLabels[setting.key] || setting.key;
    const isUpdating = updatingKeys.has(setting.key);
    const switchId = `setting-${setting.key}`;
    const descId = setting.description ? `${switchId}-desc` : undefined;
    return (
      <li
        key={setting.key}
        className="flex items-center justify-between gap-4 border-t border-border-soft px-[18px] py-4 first:border-t-0"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <Label htmlFor={switchId} className="text-sm">
            {label}
          </Label>
          {setting.key !== label && (
            <span className="font-mono text-xs text-text-3">{setting.key}</span>
          )}
          {setting.description && (
            <p id={descId} className="m-0 text-[13px] text-text-2">
              {setting.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isUpdating && <Loader2 className="size-4 animate-spin text-text-2" aria-label="Сохраняется" />}
          <Switch
            id={switchId}
            aria-describedby={descId}
            checked={setting.value}
            onCheckedChange={(checked) => handleToggle(setting.key, checked)}
            disabled={isUpdating}
          />
        </div>
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Настройки"
        subtitle="Глобальные переключатели приложения — применяются сразу"
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus aria-hidden />
            Создать настройку
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {isLoading ? (
          <ul className="m-0 list-none p-0" aria-busy>
            {Array.from({ length: 3 }, (_, i) => (
              <li key={i} className="flex items-center justify-between gap-4 border-t border-border-soft px-[18px] py-4 first:border-t-0">
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="h-6 w-10 rounded-full" />
              </li>
            ))}
          </ul>
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={loadSettings} />
        ) : settings.length === 0 ? (
          <EmptyState
            icon={<SlidersHorizontal />}
            title="Настроек нет"
            action={<Button variant="secondary" onClick={() => setIsCreateDialogOpen(true)}>Создать настройку</Button>}
          />
        ) : (
          <ul className="m-0 list-none p-0">
            {settings.map(renderSettingRow)}
          </ul>
        )}
      </Card>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            resetCreateForm();
          }
        }}
      >
        <DialogContent className="p-0">
          <DialogHeader bordered>
            <DialogTitle>Создать настройку</DialogTitle>
            <DialogDescription>Новый глобальный переключатель</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleCreate();
            }}
          >
            <DialogBody>
              <FormField label="Ключ" required error={keyError} hint="Как его читает приложение, например showOnlyRevenueCat">
                <Input
                  value={createForm.key}
                  onChange={(e) => {
                    setCreateForm(prev => ({ ...prev, key: e.target.value }));
                    if (keyError) setKeyError(null);
                  }}
                  placeholder="newSettingKey"
                  autoFocus
                />
              </FormField>
              <FormField label="Описание">
                <Input
                  value={createForm.description || ''}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Краткое описание"
                />
              </FormField>
              <div className="flex items-center gap-3">
                <Switch
                  id="new-setting-value"
                  checked={createForm.value}
                  onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, value: checked }))}
                />
                <Label htmlFor="new-setting-value">Включено</Label>
              </div>
            </DialogBody>
            <DialogFooter bordered>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreateForm();
                }}
                disabled={isCreating}
              >
                Отмена
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating && <Loader2 className="animate-spin" aria-hidden />}
                Создать
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
